import { filter, takeUntil } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import { Component, EventEmitter, Inject, Output, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';

import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Component({
  selector: 'calsoft-image-zoom',
  templateUrl: './image-zoom.component.html',
  styleUrls: ['./image-zoom.component.scss']
})
export class ImageZoomComponent implements OnInit, OnDestroy {


  url = '';
  sanitizedUrl = '';

    constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<ImageZoomComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any, 
    private router: Router,
    private sanitizer: DomSanitizer
  ) {
    this.url = this.router.url || '';
    this.sanitizedUrl = this.sanitizeUrl(this.url);
    this.router.events.pipe(filter((e: any) => e instanceof NavigationEnd), takeUntil(this._onDestroy)).subscribe(() => {
      this.url = this.router.url || '';
      this.sanitizedUrl = this.sanitizeUrl(this.url);
    });
  }


  close() {
    this.dialogRef.close();
  }

  safeMediaUrl: any;

  ngOnInit() {
    if (this.data.data && typeof this.data.data === 'string') {
      // Remove any surrounding double or single quotes
      this.data.data = this.data.data.replace(/^["']|["']$/g, '').trim();
    }
    this.detectType();
    if (this.data.data) {
      this.safeMediaUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.data.data);
    }
    console.log('Dialog Media Data:', this.data);
  }

  detectType() {
    if (this.data.type) {
      this.data.type = this.data.type.toLowerCase();
    } else {
      // Guess from extension if type is missing
      const url = this.data.data || '';
      const ext = url.split('.').pop()?.split('?')[0]?.toLowerCase() || '';
      
      if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) {
        this.data.type = 'image';
      } else if (['mp4', 'mov', 'webm', 'ogg', 'mkv', 'avi'].includes(ext)) {
        this.data.type = 'video';
      } else if (['mp3', 'wav', 'm4a', 'aac', 'flac'].includes(ext)) {
        this.data.type = 'audio';
      } else if (['pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt'].includes(ext)) {
        this.data.type = 'document';
      } else {
        this.data.type = 'image'; // Fallback
      }
    }

    // Parse item data for the card UI
    this.parseItemData();
  }

  parsedData: any = null;
  caption: string = '';

  parseItemData() {
    const item = this.data.item;
    if (!item) return;

    // Initialize basic data
    this.parsedData = {
      messagedAt: item.messagedAt || item.createdAt || new Date(),
      status: item.status || 'read',
      isIncoming: item.isIncomingMessage !== 0,
      interactive: { buttons: [] },
      template: { buttons: [] }
    };

    if (!item.data) {
       this.caption = item.message || item.bodyText || item.WebHookText || item.webhookText || '';
       return;
    }

    try {
      const parsed = typeof item.data === 'string' ? JSON.parse(item.data) : item.data;
      
      // 1. Media Values / Caption (Direct or media_values)
      this.caption = parsed.media_values?.caption || parsed.caption || '';

      // 2. Interactive Message Data
      const interactive = parsed.interaction_message_data || parsed.interactive;
      if (interactive) {
        const rawButtons = interactive.buttons || interactive.action?.buttons || interactive.reply_buttons || [];
        this.parsedData.interactive = {
          type: interactive.interactive_type || interactive.type,
          headerText: interactive.header_text || interactive.header?.text,
          bodyText: interactive.body_text || interactive.body?.text,
          footerText: interactive.footer_text || interactive.footer?.text,
          buttons: this.getButtons(rawButtons),
          listData: interactive.list_data || interactive.action || null,
          ctaData: interactive.cta_url || interactive.action?.parameters || null
        };

        // Normalize list data specifically (handle both ARRAY and OBJECT structures)
        if (this.parsedData.interactive.listData) {
          const ld = this.parsedData.interactive.listData;
          
          let rawSections = ld.sections || [];
          // If sections is an object, convert to array
          if (rawSections && !Array.isArray(rawSections) && typeof rawSections === 'object') {
            rawSections = Object.values(rawSections);
          }
          if (!Array.isArray(rawSections)) rawSections = [];
          
          this.parsedData.interactive.listData = {
            buttonText: ld.button_text || ld.buttonText || ld.button || 'Select',
            sections: rawSections.map((s: any) => {
                let rows = s.rows || [];
                // If rows is an object, convert to array
                if (rows && !Array.isArray(rows) && typeof rows === 'object') {
                  rows = Object.values(rows);
                }
                if (!Array.isArray(rows)) rows = [];

                return {
                  title: s.title || '',
                  rows: rows
                };
            })
          };
        }

        if (!this.caption) this.caption = this.parsedData.interactive.bodyText || '';
      }

      // 3. Template Components
      if (parsed.template_proforma || parsed.template_components) {
        const components = parsed.template_proforma?.components || parsed.template_components || [];
        this.parsedData.template = {
          header: components.find((c: any) => (c.type || '').toUpperCase() === 'HEADER'),
          body: components.find((c: any) => (c.type || '').toUpperCase() === 'BODY'),
          footer: components.find((c: any) => (c.type || '').toUpperCase() === 'FOOTER'),
          buttons: this.getButtons(components.find((c: any) => (c.type || '').toUpperCase() === 'BUTTONS')?.buttons)
        };

        // Handle template parameters (interpolation)
        if (parsed.template_component_values) {
           const bodyVal = parsed.template_component_values.find((v: any) => v.type?.toLowerCase() === 'body');
           if (bodyVal && bodyVal.parameters && this.parsedData.template.body) {
              let text = this.parsedData.template.body.text || '';
              bodyVal.parameters.forEach((p: any, i: number) => {
                if (p.text) text = text.replace(new RegExp(`{{\\s*${i + 1}\\s*}}`, 'g'), p.text);
              });
              this.parsedData.template.body.text = text;
           }
        }
        if (!this.caption) this.caption = this.parsedData.template.body?.text || '';
      }
      
      // Handle "Interactive" directly if it's the top key (common in some hooks)
      if (parsed.interactive && !this.parsedData.interactive.type) {
         const i = parsed.interactive;
         this.parsedData.interactive.buttons = i.action?.buttons || i.buttons || [];
         if (!this.caption) this.caption = i.body?.text || i.bodyText || '';
      }

      // Final Fallback for caption if still empty
      if (!this.caption) {
        this.caption = item.message || item.bodyText || item.WebHookText || '';
      }
    } catch (e) {
      console.error('Error parsing ImageZoom item data', e);
      this.caption = item.message || item.bodyText || item.WebHookText || '';
    }
  }

  getButtons(buttons: any): string[] {
    if (!buttons) return [];
    
    // Handle stringified JSON
    if (typeof buttons === 'string') {
      try {
        const b = JSON.parse(buttons);
        return this.getButtons(b);
      } catch (e) {
        return [buttons];
      }
    }

    // Convert Object to Array if it's not already an array
    let buttonArray = buttons;
    if (buttons && !Array.isArray(buttons) && typeof buttons === 'object') {
      buttonArray = Object.values(buttons);
    }

    if (Array.isArray(buttonArray)) {
      return buttonArray.map(b => {
        if (typeof b === 'string') return b;
        // Check various button title locations in WhatsApp JSON
        return b.text || b.title || b.display_text || b.reply?.title || b.reply?.text || b.call_to_action?.display_text || 'Button';
      });
    }
    
    return [];
  }

  getDocumentIcon(ext: string): string {
     const e = (ext || '').toLowerCase();
     if (['pdf'].includes(e)) return 'picture_as_pdf';
     if (['doc', 'docx'].includes(e)) return 'description';
     if (['xls', 'xlsx'].includes(e)) return 'table_chart';
     return 'insert_drive_file';
  }

  formatMessageTime(date: any): string {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
  }



  sanitizeUrl(u: string): string {
    return (u || '')
      .replace(/^\/+/, '')
      .replace(/[?#&=]/g, '-')
      .replace(/[^a-zA-Z0-9_-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase();
  }

    protected _onDestroy = new Subject<void>();

    ngOnDestroy() {
        this._onDestroy.next();
        this._onDestroy.complete();
    }
}
